// ============================================
// pages/ChangePassword.tsx — First-login / forced password change
// ============================================
import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useAuth, getPostLoginPath } from '../contexts/AuthContext';
import { authAPI } from '../api/services';

export const ChangePassword: React.FC = () => {
  const { user, isAuthenticated, markPasswordChanged } = useAuth();
  const navigate = useNavigate();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (oldPassword === newPassword) {
      setError('New password must be different from the current one.');
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await authAPI.changePassword(oldPassword, newPassword);
      // Replace the stale tokens so the next request's JWT claim has must_change_password=false
      if (data?.access_token) localStorage.setItem('markfed_access_token', data.access_token);
      if (data?.refresh_token) localStorage.setItem('markfed_refresh_token', data.refresh_token);
      markPasswordChanged();
      setSuccess(true);
      // Give user a moment to see the confirmation, then redirect
      setTimeout(() => navigate(getPostLoginPath(user.role), { replace: true }), 900);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Could not change password.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-blue-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src={process.env.PUBLIC_URL + '/markfed-logo.png'}
            alt="TG MARKFED"
            className="w-16 h-16 object-contain mx-auto mb-3"
          />
          <h1 className="text-xl font-bold text-gray-900">Set a new password</h1>
          <p className="text-sm text-gray-500 mt-1">
            Signed in as <span className="font-medium">{user.email}</span>
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-8">
          {user.must_change_password && (
            <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>You're using a temporary password. Please choose a new one to continue.</span>
            </div>
          )}

          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>Password updated. Redirecting…</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  disabled={submitting || success}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
              <input
                type={show ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                disabled={submitting || success}
                minLength={8}
              />
              <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
              <input
                type={show ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                disabled={submitting || success}
              />
            </div>

            <button
              type="submit"
              disabled={submitting || success}
              className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
